import ArrowDownIcon from "@redotech/redo-icons/arrow-down_filled.svg";
import ArrowLeftIcon from "@redotech/redo-icons/arrow-left_filled.svg";
import ArrowRightIcon from "@redotech/redo-icons/arrow-right_filled.svg";
import ArrowUpIcon from "@redotech/redo-icons/arrow-up_filled.svg";
import { interleave } from "@redotech/util/array";
import { browserIsMac } from "@redotech/web-util/browser";
import {
  AltKey,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ControlKey,
  EscapeKey,
  MetaKey,
  ShiftKey,
} from "@redotech/web-util/key";
import { type ComponentType, memo, useMemo } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { TextSizeValue } from "../../theme/typography";
import * as commandCss from "./redo-command-menu.module.css";

const redoCommandShortcutSize = ["sm", "md", "lg"] as const;
export type RedoCommandShortcutSize = (typeof redoCommandShortcutSize)[number];

export const RedoCommandShortcut = memo(function RedoCommandShortcut({
  hotkey,
  size = "sm",
  background = false,
}: {
  hotkey: (string | number)[] | string;
  size?: RedoCommandShortcutSize;
  /** Render each shortcut as a filled key-cap chip instead of bare text. */
  background?: boolean;
}) {
  const isMac = browserIsMac();

  const trueHotkeys: string[] = useMemo(() => {
    if (Array.isArray(hotkey)) {
      return hotkey.flatMap((key) => {
        return key.toString();
      });
    }
    return hotkey.split("");
  }, [hotkey]);

  return (
    <Flex
      align="center"
      className={
        background
          ? `${commandCss.shortcutContainer} ${commandCss.shortcutContainerFilled}`
          : commandCss.shortcutContainer
      }
      gap="none"
      px="sm"
      py="xxs"
    >
      {interleave(
        trueHotkeys.map((key) => {
          const display = getKeyDisplay(key, isMac);
          if (typeof display === "string") {
            return (
              <Text
                fontSize={sizeToFontSize[size]}
                key={key}
                textColor="placeholder"
              >
                {display}
              </Text>
            );
          } else {
            const Icon = display;
            return <Icon className={sizeToIconClass[size]} key={key} />;
          }
        }),
        (_, idx) => {
          return (
            <Text
              fontSize={sizeToFontSize[size]}
              key={`+${idx}`}
              textColor="placeholder"
            >
              +
            </Text>
          );
        },
      )}
    </Flex>
  );
});

const sizeToFontSize: Record<RedoCommandShortcutSize, TextSizeValue> = {
  sm: "xxs",
  md: "xs",
  lg: "sm",
};

const sizeToIconClass: Record<RedoCommandShortcutSize, string> = {
  sm: commandCss.shortcutIconSm as string,
  md: commandCss.shortcutIconMd as string,
  lg: commandCss.shortcutIconLg as string,
};

function getKeyDisplay(
  key: string,
  isMac: boolean,
): string | ComponentType<{ className?: string }> {
  if (key === ControlKey || key === MetaKey || key === "⌘" || key === "Ctrl") {
    return isMac ? "⌘" : "Ctrl";
  }
  if (key === AltKey || key === "⌥" || key === "Alt") {
    return isMac ? "⌥" : "Alt";
  }
  if (key === ShiftKey) {
    return "⇧";
  }
  if (key === EscapeKey) {
    return "ESC";
  }
  if (key === ArrowUp) {
    return ArrowUpIcon;
  }
  if (key === ArrowDown) {
    return ArrowDownIcon;
  }
  if (key === ArrowRight) {
    return ArrowRightIcon;
  }
  if (key === ArrowLeft) {
    return ArrowLeftIcon;
  }
  return key;
}
